import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api, PHP_VERSIONS } from '../lib/api'
import { Card, Button, Modal, Spinner, Field, Input, Select, Checkbox, Badge } from './ui'
import { IconPlus, IconTrash } from './icons'

interface PHPVer {
  version: string
  fpm: boolean; cli: boolean; cgi: boolean; apache_mod: boolean; is_default: boolean
}
interface PHPIni {
  'date.timezone': string
  memory_limit: string
  upload_max_filesize: string
  post_max_size: string
  max_execution_time: string
  max_input_time: string
  display_errors: boolean
  log_errors: boolean
  error_reporting: string
}
interface PHPExt {
  name: string; installed: boolean; enabled: boolean; locked: boolean
}

export function PHPManager({ nodeId }: { nodeId: string }) {
  const qc = useQueryClient()
  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['php-versions', nodeId],
    queryFn: async () => (await api.get<PHPVer[]>(`/nodes/${nodeId}/php-versions`)).data,
    refetchInterval: 15000,
  })
  const [addOpen, setAddOpen] = useState(false)
  const [iniFor, setIniFor] = useState<string | null>(null)
  const [extFor, setExtFor] = useState<string | null>(null)

  const removeVer = useMutation({
    mutationFn: async (v: string) => (await api.delete(`/nodes/${nodeId}/php-versions/${v}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['php-versions', nodeId] }),
  })

  if (isLoading) return <Card className="p-8 text-center"><Spinner /></Card>

  return (
    <Card className="mb-5 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
        <div>
          <h2 className="text-[15px] font-semibold">PHP</h2>
          <div className="text-[12px] text-neutral-500">Версии, режимы обработки, настройки, расширения</div>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}><IconPlus /> Добавить версию</Button>
      </div>
      <table className="w-full text-[13px]">
        <thead className="bg-neutral-50/60 border-b border-neutral-100 text-[11px] uppercase text-neutral-500 tracking-wider">
          <tr>
            <th className="text-left font-medium px-5 py-2">Версия</th>
            <th className="text-center font-medium px-3 py-2">FPM</th>
            <th className="text-center font-medium px-3 py-2">CLI</th>
            <th className="text-center font-medium px-3 py-2">CGI</th>
            <th className="text-center font-medium px-3 py-2">mod_apache</th>
            <th className="text-right font-medium px-5 py-2">Действия</th>
          </tr>
        </thead>
        <tbody>
          {versions.length === 0 && (
            <tr><td colSpan={6} className="px-5 py-8 text-center text-neutral-400 text-[12px]">Нет установленных версий</td></tr>
          )}
          {versions.map(v => (
            <tr key={v.version} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
              <td className="px-5 py-2.5 font-mono">
                PHP {v.version}
                {v.is_default && <span className="ml-2"><Badge tone="blue">default</Badge></span>}
              </td>
              <td className="text-center">{v.fpm ? <span className="text-green-600">✓</span> : <span className="text-neutral-300">·</span>}</td>
              <td className="text-center">{v.cli ? <span className="text-green-600">✓</span> : <span className="text-neutral-300">·</span>}</td>
              <td className="text-center">{v.cgi ? <span className="text-green-600">✓</span> : <span className="text-neutral-300">·</span>}</td>
              <td className="text-center">{v.apache_mod ? <span className="text-green-600">✓</span> : <span className="text-neutral-300">·</span>}</td>
              <td className="px-5 py-2 text-right">
                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setIniFor(v.version)}>php.ini</Button>
                  <Button variant="ghost" size="sm" onClick={() => setExtFor(v.version)}>Расширения</Button>
                  <Button variant="ghost" size="sm" title="Удалить"
                          onClick={() => confirm(`Удалить PHP ${v.version} со всеми пакетами?`) && removeVer.mutate(v.version)}>
                    <IconTrash />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {addOpen && <AddVersionModal nodeId={nodeId} existing={versions.map(x=>x.version)} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['php-versions', nodeId] }) }} />}
      {iniFor && <IniModal nodeId={nodeId} version={iniFor} onClose={() => setIniFor(null)} />}
      {extFor && <ExtModal nodeId={nodeId} version={extFor} onClose={() => setExtFor(null)} />}
    </Card>
  )
}

function AddVersionModal({ nodeId, existing, onClose, onDone }: {
  nodeId: string; existing: string[]; onClose: () => void; onDone: () => void
}) {
  const missing = PHP_VERSIONS.filter(v => !existing.includes(v))
  const [version, setVersion] = useState(missing[0] || '8.4')
  const [handlers, setHandlers] = useState<Record<string, boolean>>({ fpm: true, cli: true })
  const install = useMutation({
    mutationFn: async () => (await api.post(`/nodes/${nodeId}/php-versions`, {
      version,
      handlers: Object.entries(handlers).filter(([,v]) => v).map(([k]) => k),
    })).data,
    onSuccess: onDone,
  })
  return (
    <Modal open onClose={onClose} title="Установить PHP-версию">
      <div className="space-y-4">
        <Field label="Версия">
          <Select value={version} onChange={e => setVersion(e.target.value)}>
            {missing.length === 0
              ? <option value="">— все уже установлены —</option>
              : missing.map(v => <option key={v} value={v}>PHP {v}</option>)}
          </Select>
        </Field>
        <div>
          <div className="text-[12px] text-neutral-500 mb-2">Handler'ы:</div>
          <div className="space-y-2">
            {[['fpm','FPM (nginx FastCGI)'],['cli','CLI (командная строка)'],['cgi','CGI'],['apache_mod','mod_php для Apache']].map(([k,l]) => (
              <Checkbox key={k} label={l as string}
                checked={!!handlers[k as string]}
                onChange={e => setHandlers({ ...handlers, [k as string]: e.target.checked })} />
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button onClick={() => install.mutate()} disabled={install.isPending || missing.length === 0}>
            {install.isPending ? 'Ставим…' : 'Установить'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function IniModal({ nodeId, version, onClose }: { nodeId: string; version: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['php-ini', nodeId, version],
    queryFn: async () => (await api.get<PHPIni>(`/nodes/${nodeId}/php-versions/${version}/ini`)).data,
  })
  const [f, setF] = useState<PHPIni | null>(null)
  useEffect(() => { if (data) setF(data) }, [data])
  const save = useMutation({
    mutationFn: async () => (await api.put(`/nodes/${nodeId}/php-versions/${version}/ini`, f)).data,
    onSuccess: onClose,
  })
  if (isLoading || !f) return <Modal open onClose={onClose} title={`Настройка PHP ${version}`}><div className="p-8"><Spinner /></div></Modal>
  return (
    <Modal open onClose={onClose} title={`Настройка PHP ${version}`}>
      <div className="space-y-3">
        <Field label="Timezone"><Input value={f['date.timezone']} onChange={e => setF({...f, 'date.timezone': e.target.value})} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="memory_limit"><Input value={f.memory_limit} onChange={e => setF({...f, memory_limit: e.target.value})} /></Field>
          <Field label="max_execution_time (сек)"><Input value={f.max_execution_time} onChange={e => setF({...f, max_execution_time: e.target.value})} /></Field>
          <Field label="upload_max_filesize"><Input value={f.upload_max_filesize} onChange={e => setF({...f, upload_max_filesize: e.target.value})} /></Field>
          <Field label="post_max_size"><Input value={f.post_max_size} onChange={e => setF({...f, post_max_size: e.target.value})} /></Field>
          <Field label="max_input_time"><Input value={f.max_input_time} onChange={e => setF({...f, max_input_time: e.target.value})} /></Field>
        </div>
        <Field label="error_reporting"><Input value={f.error_reporting} onChange={e => setF({...f, error_reporting: e.target.value})} /></Field>
        <Checkbox label="display_errors" checked={f.display_errors} onChange={e => setF({...f, display_errors: e.target.checked})} />
        <Checkbox label="log_errors" checked={f.log_errors} onChange={e => setF({...f, log_errors: e.target.checked})} />
        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Сохраняем…' : 'Сохранить'}</Button>
        </div>
      </div>
    </Modal>
  )
}

function ExtModal({ nodeId, version, onClose }: { nodeId: string; version: string; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: exts = [], isLoading } = useQuery({
    queryKey: ['php-ext', nodeId, version],
    queryFn: async () => (await api.get<PHPExt[]>(`/nodes/${nodeId}/php-versions/${version}/extensions`)).data,
  })
  const [filter, setFilter] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const toggle = async (ext: PHPExt) => {
    setBusy(ext.name)
    try {
      if (ext.installed) {
        await api.delete(`/nodes/${nodeId}/php-versions/${version}/extensions/${ext.name}`)
      } else {
        await api.post(`/nodes/${nodeId}/php-versions/${version}/extensions`, { name: ext.name })
      }
      setTimeout(() => qc.invalidateQueries({ queryKey: ['php-ext', nodeId, version] }), 2000)
    } finally { setBusy(null) }
  }

  const q = filter.toLowerCase()
  const filtered = exts.filter(x => !q || x.name.toLowerCase().includes(q))

  return (
    <Modal open onClose={onClose} title={`Расширения PHP ${version}`} size="lg">
      <div className="mb-3">
        <Input placeholder="Поиск расширения…" value={filter} onChange={e => setFilter(e.target.value)} />
      </div>
      {isLoading ? <div className="p-8 text-center"><Spinner /></div> : (
        <div className="max-h-[500px] overflow-y-auto border border-neutral-200 rounded-md">
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50 sticky top-0 border-b border-neutral-200 text-[11px] uppercase text-neutral-500 tracking-wider">
              <tr>
                <th className="text-left font-medium px-4 py-2">Расширение</th>
                <th className="text-center font-medium px-3 py-2">Статус</th>
                <th className="text-right font-medium px-4 py-2 w-[120px]">Действие</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(ext => (
                <tr key={ext.name} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/40">
                  <td className="px-4 py-2 font-mono">{ext.name}</td>
                  <td className="text-center">
                    {ext.locked ? <Badge>🔒 системное</Badge>
                      : ext.enabled ? <Badge tone="green">включено</Badge>
                      : ext.installed ? <Badge tone="yellow">выкл</Badge>
                      : <Badge>не установлено</Badge>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {ext.locked ? <span className="text-neutral-400 text-[12px]">—</span> :
                      <Button variant="ghost" size="sm" disabled={busy === ext.name}
                              onClick={() => toggle(ext)}>
                        {busy === ext.name ? '…' : (ext.installed ? 'Удалить' : 'Установить')}
                      </Button>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-neutral-400">Ничего не найдено</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-between items-center mt-3 pt-3 border-t border-neutral-100">
        <div className="text-[12px] text-neutral-500">
          Всего: {exts.length} · включено: {exts.filter(x=>x.enabled).length}
        </div>
        <Button variant="ghost" onClick={onClose}>Закрыть</Button>
      </div>
    </Modal>
  )
}
